import { useEffect, useState } from "react";
import { Gift, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PromoCodes } from "@/components/PromoCodes";
import { api, errMsg } from "@/lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const badge = (s) => ({ active: "bg-emerald-50 text-emerald-700 border-emerald-200", trialing: "bg-blue-50 text-blue-700 border-blue-200", trial: "bg-blue-50 text-blue-700 border-blue-200", past_due: "bg-red-50 text-red-700 border-red-200", canceled: "bg-slate-100 text-slate-700 border-slate-200" }[s] || "bg-amber-50 text-amber-700 border-amber-200");

export default function AdminPage() {
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e))); }, []);

  const comp = async (u, months) => {
    setBusy(u.id + months);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/comp`, { months });
      setUsers((l) => l.map((x) => (x.id === u.id ? data : x)));
      toast.success(months ? `${u.email}: free access until ${fmt(data.comp_until_raw)}` : `${u.email}: free access removed`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Subscribers</h1>
        <p className="text-sm text-muted-foreground mt-1">Every business on Basta10. Give free months to friends, testers or referrals.</p>
      </div>
      {!users ? <p className="text-sm text-muted-foreground">Loading...</p> : (
        <div className="surface overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead><tr className="border-b border-border text-xs font-bold uppercase tracking-wider text-muted-foreground text-left">
              <th className="px-4 py-3">Business</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Plan / ends</th><th className="px-4 py-3">Free access</th><th className="px-4 py-3">Give free months</th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-b border-border last:border-0">
                  <td className="px-4 py-3"><div className="font-medium">{u.business_name}</div><div className="text-xs text-muted-foreground">{u.email}{u.is_admin ? " · owner" : ""}</div></td>
                  <td className="px-4 py-3"><span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${badge(u.status)}`} data-testid={`admin-user-status-${u.id}`}>{u.is_admin ? "owner" : u.status}</span></td>
                  <td className="px-4 py-3 text-xs">{u.plan ? (u.plan === "pro_yearly" ? "Yearly" : "Monthly") : "—"}{u.trial_end && (u.status === "trialing" || u.status === "trial") ? ` · trial ends ${fmt(u.trial_end)}` : u.current_period_end ? ` · ${fmt(u.current_period_end)}` : ""}</td>
                  <td className="px-4 py-3 text-xs" data-testid={`admin-user-comp-${u.id}`}>{u.comp_until ? <span className="inline-flex items-center gap-1 text-emerald-600 font-medium"><Gift className="h-3.5 w-3.5" /> until {fmt(u.comp_until)}</span> : "—"}</td>
                  <td className="px-4 py-3">
                    {u.is_admin ? <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> always free</span> : (
                      <div className="flex gap-1">
                        {[1, 2, 3].map((m) => <Button key={m} size="sm" variant="outline" data-testid={`admin-comp-${m}-${u.id}`} disabled={!!busy} onClick={() => comp(u, m)} className="h-8 px-2.5 text-xs">+{m} mo</Button>)}
                        {u.comp_until && <Button size="sm" variant="ghost" data-testid={`admin-comp-clear-${u.id}`} disabled={!!busy} onClick={() => comp(u, 0)} className="h-8 px-2 text-xs text-red-600">Clear</Button>}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <PromoCodes />
    </div>
  );
}
